import { z } from 'zod';
import type { SiteDocument } from '../types/siteRag';
import { documentForSiteRecord } from './siteDocumentPersistence';
import { hashCanonicalJson } from './reportGeneration/reportContractHash';
import { patchInvalidatesImportance } from './docImportance';

const id = z.string().min(1).max(160).regex(/^[A-Za-z0-9_-]+$/);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const epoch = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
/** Ordinary user metadata only. Extractor/source/provenance authority is excluded. */
export const siteDocumentMetadataPatchSchema = z.object({
  fileName: z.string().trim().min(1).max(512).optional(),
  summary: z.string().max(20_000).optional(),
  tags: z.array(z.string().min(1).max(128)).max(100).optional(),
  folder: z.string().max(200).optional(),
  movedAt: epoch.optional(),
  priority: z.boolean().optional(),
  includeInSed: z.boolean().optional(),
  includeInReport: z.boolean().optional(),
  importanceScore: z.number().finite().min(0).max(1).nullable().optional(),
  importanceScoreAt: epoch.nullable().optional(),
}).strict().refine(patch => Object.keys(patch).length > 0, 'Enter a metadata change.');
export type SiteDocumentMetadataPatch = z.infer<typeof siteDocumentMetadataPatchSchema>;
export const siteDocumentMetadataRequestSchema = z.object({
  operationId: z.string().uuid(), ownerUid: id, siteId: id, documentId: id,
  expectedDocumentHash: digest, patch: siteDocumentMetadataPatchSchema,
}).strict();
export type SiteDocumentMetadataRequest = z.infer<typeof siteDocumentMetadataRequestSchema>;

// This is the already-required persisted core; passthrough retains every private
// source/extractor field supplied by the server, never accepted from the client.
const documentSchema = z.object({
  id, fileName: z.string(), mimeType: z.string(), sizeBytes: z.number().nonnegative(),
  summary: z.string(), tags: z.array(z.string()), textContent: z.string(), uploadedAt: z.string(),
}).passthrough();
export const siteDocumentMetadataAckSchema = z.object({
  saved: z.literal(true), operationId: z.string().uuid(), ownerUid: id, siteId: id, documentId: id,
  beforeDocumentHash: digest, afterDocumentHash: digest, writtenAt: z.string().datetime(),
  document: documentSchema,
  clearedFields: z.array(z.enum(['importanceScore', 'importanceScoreAt'])),
}).strict();
export type SiteDocumentMetadataAck = z.infer<typeof siteDocumentMetadataAckSchema>;

/** Hash the same compact site-record view even when a browser has hydrated bodies. */
export function siteDocumentMetadataBasisHash(document: SiteDocument): string {
  return hashCanonicalJson(documentForSiteRecord(document));
}
/** Preserve every source/body identity field; clear only explicitly optional metadata. */
export function applySiteDocumentMetadataPatch(document: SiteDocument,
  patch: SiteDocumentMetadataPatch, writtenAt: string): SiteDocument {
  const next: SiteDocument = { ...document, updatedAt: writtenAt };
  const { importanceScore, importanceScoreAt, ...ordinary } = patch;
  Object.assign(next, ordinary);
  if (Object.prototype.hasOwnProperty.call(patch, 'importanceScore')) {
    if (importanceScore === null) delete next.importanceScore;
    else next.importanceScore = importanceScore;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'importanceScoreAt')) {
    if (importanceScoreAt === null) delete next.importanceScoreAt;
    else next.importanceScoreAt = importanceScoreAt;
  }
  // Same derived cache invalidation as the ordinary existing client writer.
  if (!Object.prototype.hasOwnProperty.call(patch, 'importanceScore') && patchInvalidatesImportance(ordinary)) {
    delete next.importanceScore;
    delete next.importanceScoreAt;
  }
  return next;
}
